import { AppType, LocationScope, LocationVarKey } from "@swivl-mono/types";
import { Button, ButtonColor, SelectItem } from "@swivl-mono/ui";
import { useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";

import LocationsAndGroupsDropdown, {
  LocationsAndGroupsDropdownType
} from "../../../Components/Dropdowns/LocationsAndGroupsDropdown/LocationsAndGroupsDropdown";
import { WorksheetColumn, WorksheetLayout, WorksheetRow } from "../../../Components/Worksheets/Layout/Worksheet.Layout.Components";
import { IS_NOT_EQUAL, PageKey } from "../../../Components/Worksheets/Types/Worksheet.Interface";
import { WorksheetComponent, WorksheetComponentType } from "../../../Components/Worksheets/WorksheetComponent/WorksheetComponent.Component";
import { useLocationVariables } from "../../../Hooks/useLocationVariables";
import { usePropertiesGetLocationIds } from "../../../Hooks/usePropertiesGetLocationIds";
import AppsModel from "../../../Models/Apps/Apps.Model";
import BrandsModel from "../../../Models/Brands/Brands.Model";
import LocationsModel from "../../../Models/Locations/Locations.Model";
import { Member } from "../../../Models/Member/Member.Current";
import ReviewsModel from "../../../Models/Reviews/Reviews.Model";

export const PAGE_KEYS: PageKey[] = [
  LocationVarKey.BrandId,
  LocationVarKey.GoogleReviewsSyncEnabled,
  { key: LocationVarKey.GoogleAppId, hideIf: IS_NOT_EQUAL(LocationVarKey.GoogleReviewsSyncEnabled, "1") },
  { key: LocationVarKey.GoogleAccountAndLocationName, hideIf: IS_NOT_EQUAL(LocationVarKey.GoogleReviewsSyncEnabled, "1") },
  LocationVarKey.TwilioAppId,
  LocationVarKey.TwilioApiKey,
  LocationVarKey.TwilioApiSecret,
  LocationVarKey.MapURL,
  LocationVarKey.WalletPassURL,
  LocationVarKey.CustomVariable1,
  LocationVarKey.CustomVariable2,
  LocationVarKey.CustomVariable3
];

export default function LocationSubpageInformation() {
  const page = usePropertiesGetLocationIds();
  const navigate = useNavigate();
  const vars = useLocationVariables([LocationVarKey.GoogleAppId], page.groupId, page.locationId); // useWorksheetVariable(LocationVarKey.GoogleAppId, page.path,  page.groupId, page.locationId);
  const googleAppId = vars?.[LocationVarKey.GoogleAppId];

  const { googleLocationsForApp } = ReviewsModel.useState((s) => s);
  const { apps } = AppsModel.useState((s) => s);
  const { brands } = BrandsModel.useState();

  useEffect(() => {
    AppsModel.load(false);
    BrandsModel.load(false);
  }, []);

  useEffect(() => {
    if (Member.orgId && googleAppId && googleAppId) {
      ReviewsModel.loadGoogleLocationsForApp(parseInt(googleAppId));
    } else {
    }
  }, [googleAppId]);

  const appOptions: { [type in AppType]?: SelectItem[] } = useMemo(() => {
    if (!apps) return {};
    var options: { [type in AppType]?: SelectItem[] } = {};
    for (let index = 0; index < apps.length; index++) {
      const element = apps[index];
      if (!options[element.type]) {
        options[element.type] = [];
      }
      options[element.type]!.push({ label: element.name, value: String(element.id) });
    }

    return options;
  }, [apps]);

  const brandOptions: SelectItem[] | undefined = useMemo(() => {
    if (!brands) return undefined;
    return Object.values(brands).map((a) => ({ label: a.name, value: String(a.id) }));
  }, [brands]);

  const googleLocationOptions = googleAppId
    ? googleLocationsForApp[parseInt(googleAppId)]?.map((a) => ({ label: a.title + " - " + a.storefrontAddress?.addressLines?.[0], value: a.name }))
    : [];

  return (
    <WorksheetLayout>
      <h4>Advanced Settings</h4>
      <WorksheetRow>
        <WorksheetColumn>
          <WorksheetComponent
            isLoading={!brandOptions}
            componentType={WorksheetComponentType.DROPDOWN}
            {...page}
            options={brandOptions}
            label="Brand"
            keyName={LocationVarKey.BrandId}
          />

          <WorksheetComponent
            componentType={WorksheetComponentType.SWITCH}
            {...page}
            label="Sync Google Reviews"
            keyName={LocationVarKey.GoogleReviewsSyncEnabled}
          />

          <WorksheetComponent
            hideIf={IS_NOT_EQUAL(LocationVarKey.GoogleReviewsSyncEnabled, "1")}
            componentType={WorksheetComponentType.DROPDOWN}
            options={appOptions[AppType.GOOGLE]}
            {...page}
            label="Google Review App"
            keyName={LocationVarKey.GoogleAppId}
          />

          <WorksheetComponent
            hideIf={IS_NOT_EQUAL(LocationVarKey.GoogleReviewsSyncEnabled, "1")}
            componentType={WorksheetComponentType.DROPDOWN}
            options={googleLocationOptions}
            {...page}
            label="Google Review Place ID"
            keyName={LocationVarKey.GoogleAccountAndLocationName}
          />

          <WorksheetComponent
            componentType={WorksheetComponentType.DROPDOWN}
            options={appOptions[AppType.TWILIO]}
            {...page}
            label="Outbound Twilio App"
            keyName={LocationVarKey.TwilioAppId}
          />

          {page.type === LocationScope.ORG && (
            <>
              <WorksheetComponent
                componentType={WorksheetComponentType.SINGLE_LINE}
                {...page}
                label="Outbound Twilio API Key"
                keyName={LocationVarKey.TwilioApiKey}
              />

              <WorksheetComponent
                componentType={WorksheetComponentType.PASSWORD}
                {...page}
                label="Outbound Twilio API Secret"
                keyName={LocationVarKey.TwilioApiSecret}
              />
            </>
          )}

          <WorksheetComponent componentType={WorksheetComponentType.SINGLE_LINE} {...page} label="Facility Map URL" keyName={LocationVarKey.MapURL} />

          <WorksheetComponent
            componentType={WorksheetComponentType.SINGLE_LINE}
            {...page}
            label="Wallet App URL"
            keyName={LocationVarKey.WalletPassURL}
          />

          <WorksheetComponent
            componentType={WorksheetComponentType.SINGLE_LINE}
            {...page}
            label="Custom Variable 1"
            keyName={LocationVarKey.CustomVariable1}
          />

          <WorksheetComponent
            componentType={WorksheetComponentType.SINGLE_LINE}
            {...page}
            label="Custom Variable 2"
            keyName={LocationVarKey.CustomVariable2}
          />

          <WorksheetComponent
            componentType={WorksheetComponentType.SINGLE_LINE}
            {...page}
            label="Custom Variable 3"
            keyName={LocationVarKey.CustomVariable3}
          />

          {page.type === LocationScope.LOCATION && (
            <>
              <label>Group</label>
              <LocationsAndGroupsDropdown
                groupId={page.group?.id}
                type={LocationsAndGroupsDropdownType.GROUPS}
                onSelect={async (groupId?: number, locationId?: number) => {
                  if (groupId && page.locationId) {
                    await LocationsModel.setGroupForLocation(page.locationId, groupId);
                    navigate(`/properties/group/${groupId}/location/${page.locationId}/page/details/advanced-settings`);
                  }
                }}
              />

              <label>Danger zone</label>
              <Button
                color={ButtonColor.WHITE}
                onClick={() => {
                  if (window.confirm("Are you sure you want to delete this location?") && page.locationId) {
                    LocationsModel.deleteLocation(page.locationId);
                    navigate(`/properties/group/${page.group?.id}/page/details/advanced-settings`);
                  }
                }}
              >
                Delete Location
              </Button>
            </>
          )}

          {page.type === LocationScope.GROUP && (
            <>
              <label>Danger zone</label>
              <Button
                color={ButtonColor.WHITE}
                onClick={() => {
                  if (window.confirm("Are you sure you want to delete this group?") && page.groupId) {
                    LocationsModel.deleteGroup(page.groupId);
                    navigate(`/properties`);
                  }
                }}
              >
                Delete Group
              </Button>
            </>
          )}
        </WorksheetColumn>
        <WorksheetColumn></WorksheetColumn>
      </WorksheetRow>
    </WorksheetLayout>
  );
}
